import { LoadingButton } from '@mui/lab';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';
import { useState } from 'react';
import { invitationApi } from 'src/api/invitation';
import { useUserContext } from 'src/auth/useUserContext';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { translateKey } from 'src/locales/translate';


interface NotificationsApproveModalProps {
  fetchInvites: () => void;
  invitationToken: string;
}

export default function NotificationsApproveModal({
  fetchInvites,
  invitationToken,
}: NotificationsApproveModalProps) {
  const { refetch: refetchUser } = useUserContext();
  const { refetch: refetchOrganizations } = useOrganizationContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: acceptInvitation } = useMutation(
    ['organization-invite-accept', invitationToken],
    () =>
      invitationApi.invitationControllerAcceptInvitation({
        processInvitationDto: { token: invitationToken },
      }),
    {
      retry: 2,
    },
  );

  const closeModal = () => setIsOpen(false);

  const openModal = () => setIsOpen(true);

  const handleAction = async () => {
    setIsLoading(true);
    try {
      await acceptInvitation();
      closeModal();
      enqueueSnackbar(translateKey("hardcoded.zaproszenie_zostalo_zaakceptowane"), {
        variant: 'success',
      });
      
      // Force Firebase token refresh to get new claims
      await getAuth().currentUser?.getIdToken(true);
      
      fetchInvites();
      refetchUser();
      refetchOrganizations();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Zaakceptuj" placement="top" arrow>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
          color="success"
        >
          <Iconify icon="ic:round-check-circle-outline" />
        </IconButton>
      </Tooltip>
      <Dialog open={isOpen} onClose={closeModal}>
        <DialogTitle>Zaakceptuj zaproszenie</DialogTitle>

        <DialogContent sx={{ maxWidth: '400px' }}>
          <DialogContentText id="alert-dialog-description">{translateKey("hardcoded.czy_na_pewno_chcesz_zaakceptowac_zaproszenie_do_placowki")}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeModal}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            onClick={handleAction}
            loading={isLoading}
            color="success"
          >
            <span>Zaakceptuj</span>
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
